import { useState, useEffect } from 'react';
import { useUserPreferences, useStoreActions, useSettingsStore } from '@/store/useStore';
import {
  Calendar, MapPin, User, Search, Bell,
  AlertCircle, Plus, Edit2, Trash2, Check, ChevronsUpDown,
} from 'lucide-react';
import { DataProcessor } from '@/utils/dataProcessor';
import { findUserProgram } from '@/utils/programMatching';
import { getOrderedDays } from '@/utils/dayUtils';
import { applyTimetableProfile } from '@/utils/timetableProfiles';
import Logo from '@/components/Logo';
import { useDashboard } from '@/contexts/DashboardContext';
import SemesterMigrationNotice from '@/components/SemesterMigrationNotice';
import GraduatedTimetableState from '@/components/GraduatedTimetableState';
import { useHasGraduated } from '@/hooks/useSemesterCompletion';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { LostAndFoundShortcut } from '@/components/LostAndFoundShortcut';

// ─── Types ────────────────────────────────────────────────────────────────────
interface CustomClass {
  id: string; day: string; time: string; course: string;
  room: string; instructor: string; type: 'makeup' | 'custom'; createdAt: number;
}

// ─── Shared primitives ────────────────────────────────────────────────────────
function numberToRoman(num: number | string): string {
  const n = typeof num === 'string' ? parseInt(num) : num;
  return (['','I','II','III','IV','V','VI','VII','VIII','IX','X'][n]) || String(num);
}

function TopBtn({ children, onClick, badge }: { children: React.ReactNode; onClick?: () => void; badge?: number }) {
  return (
    <button onClick={onClick} style={{
      width: 32, height: 32, borderRadius: 9, flexShrink: 0,
      background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.07)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'rgba(255,255,255,.5)', cursor: 'pointer', position: 'relative',
    }}>
      {children}
      {badge && badge > 0 ? (
        <span style={{
          position: 'absolute', top: -3, right: -3, width: 13, height: 13,
          borderRadius: '50%', background: '#EF4444', border: '1.5px solid #0A0A0A',
          fontSize: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, color: '#fff',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

function SectionHeader({ title, meta }: { title: string; meta?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <span style={{ fontFamily: "'Instrument Serif',serif", fontSize: 17, fontWeight: 400, letterSpacing: '-.01em' }}>{title}</span>
      {meta && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.12em', color: 'rgba(255,255,255,.25)' }}>{meta}</span>}
    </div>
  );
}

function EmptyState({ text, onAdd }: { text: string; onAdd?: () => void }) {
  return (
    <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 12, padding: '24px 16px', textAlign: 'center' }}>
      <Calendar size={20} color="rgba(255,255,255,.2)" style={{ margin: '0 auto 8px' }} aria-hidden />
      <p style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>{text}</p>
      {onAdd && (
        <button onClick={onAdd} style={{ marginTop: 10, fontSize: 11, color: '#14B8A6', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>
          Add one now →
        </button>
      )}
    </div>
  );
}

// ─── Class card ───────────────────────────────────────────────────────────────
function ClassCard({ cls, isCustom = false, onEdit, onDelete }: {
  cls: any; isCustom?: boolean; onEdit?: () => void; onDelete?: () => void;
}) {
  const accent = isCustom ? (cls.type === 'makeup' ? '#F59E0B' : '#22C55E') : '#14B8A6';
  const [startTime, endTime] = cls.time ? cls.time.split('-').map((t: string) => t.trim()) : ['', ''];
  
  return (
    <div style={{
      background: 'rgba(255,255,255,.03)',
      border: '1px solid rgba(255,255,255,.07)',
      borderRadius: 12, padding: '14px 16px',
      display: 'flex', alignItems: 'flex-start', gap: 14,
    }}>
      {/* Time column */}
      <div style={{ minWidth: 52, textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: 'rgba(255,255,255,.6)', lineHeight: 1 }}>
          {startTime}
        </div>
        {endTime && (
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,.25)', marginTop: 2 }}>
            {endTime}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{
        width: 1, alignSelf: 'stretch', margin: '2px 0', flexShrink: 0,
        background: 'rgba(255,255,255,.08)',
      }} />

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cls.course}
          </span>
          {isCustom && (
            <span style={{
              fontFamily: "'JetBrains Mono',monospace", fontSize: 7, letterSpacing: '.07em',
              padding: '2px 5px', borderRadius: 3, flexShrink: 0,
              background: cls.type === 'makeup' ? 'rgba(245,158,11,.1)' : 'rgba(34,197,94,.1)',
              border: `1px solid ${cls.type === 'makeup' ? 'rgba(245,158,11,.2)' : 'rgba(34,197,94,.2)'}`,
              color: cls.type === 'makeup' ? '#F59E0B' : '#22C55E',
            }}>{cls.type === 'makeup' ? 'MAKEUP' : 'CUSTOM'}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {cls.instructor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,.35)', fontWeight: 300 }}>
              <User size={11} color="rgba(20,184,166,.6)" aria-hidden /> {cls.instructor}
            </div>
          )}
          {cls.room && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,.35)', fontWeight: 300 }}>
              <MapPin size={11} color="rgba(20,184,166,.6)" aria-hidden /> {cls.room}
            </div>
          )}
        </div>
      </div>

      {/* Actions or accent indicator */}
      {isCustom ? (
        <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
          <button onClick={onEdit} style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(96,165,250,.08)', border: '1px solid rgba(96,165,250,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60A5FA', cursor: 'pointer' }}>
            <Edit2 size={10} aria-hidden />
          </button>
          <button onClick={onDelete} style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444', cursor: 'pointer' }}>
            <Trash2 size={10} aria-hidden />
          </button>
        </div>
      ) : (
        <div style={{
          width: 3, height: 24, borderRadius: 2, flexShrink: 0,
          background: accent,
        }} />
      )}
    </div>
  );
}

// ─── Week cell ────────────────────────────────────────────────────────────────
function WeekCell({ cls }: { cls: any }) {
  return (
    <div style={{ background: 'rgba(20,184,166,.07)', border: '1px solid rgba(20,184,166,.14)', borderRadius: 8, padding: '8px 9px' }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, color: '#14B8A6', marginBottom: 3 }}>
        {cls.time?.split('-')[0]?.trim()}
      </div>
      <div style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
        {cls.course}
      </div>
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,.3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {cls.room}
      </div>
    </div>
  );
}

// ─── Combobox field (mobile-optimised) ───────────────────────────────────────
function ComboField({ label, value, options, onChange, required = false }: {
  label: string; value: string; options: string[]; onChange: (v: string) => void; required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 6 }}>
        {label.toUpperCase()}{required && <span style={{ color: 'rgba(239,68,68,.6)' }}> *</span>}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)',
            borderRadius: 9, padding: '10px 12px', fontSize: 12,
            color: value && value !== 'custom' ? '#fff' : 'rgba(255,255,255,.3)',
            fontFamily: "'Outfit',sans-serif", cursor: 'pointer',
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
              {value && value !== 'custom' ? value : `Select ${label.toLowerCase()}…`}
            </span>
            <ChevronsUpDown size={12} color="rgba(255,255,255,.3)" style={{ flexShrink: 0, marginLeft: 6 }} />
          </button>
        </PopoverTrigger>
        <PopoverContent style={{ background: '#161616', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: 0, width: 280 }}>
          <Command>
            <CommandInput placeholder="Search…" style={{ background: 'transparent', color: '#fff', fontSize: 12 }} />
            <CommandList style={{ maxHeight: 200 }}>
              <CommandEmpty style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', padding: 12 }}>Nothing found.</CommandEmpty>
              <CommandGroup>
                {options.map(opt => (
                  <CommandItem key={opt} value={opt} onSelect={v => { onChange(v); setOpen(false); }}
                    style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', cursor: 'pointer' }}>
                    <Check size={11} className={cn('mr-2', value === opt ? 'opacity-100' : 'opacity-0')} />
                    {opt}
                  </CommandItem>
                ))}
                <CommandItem value="custom" onSelect={() => { onChange('custom'); setOpen(false); }}
                  style={{ fontSize: 12, color: 'rgba(20,184,166,.7)', cursor: 'pointer' }}>
                  <Plus size={11} className="mr-2" /> Custom…
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value === 'custom' && (
        <input placeholder={`Enter ${label.toLowerCase()}`} onChange={e => onChange(e.target.value)}
          style={{ marginTop: 7, width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(20,184,166,.3)', borderRadius: 9, padding: '10px 12px', fontSize: 12, color: '#fff', fontFamily: "'Outfit',sans-serif", outline: 'none' }} />
      )}
    </div>
  );
}

// ─── Main mobile page ─────────────────────────────────────────────────────────
export default function PersonalTimetableMobile() {
  const dashboard       = useDashboard();
  const userPreferences = useUserPreferences();
  const { initializeData } = useStoreActions();
  const { preferences }    = useSettingsStore();
  const { toast }          = useToast();

  const { graduated, semesterLabel: gradSemesterLabel } = useHasGraduated(userPreferences);

  const [selectedDay,   setSelectedDay]   = useState(new Date().toLocaleDateString('en-US', { weekday: 'long' }));
  const [viewMode,      setViewMode]      = useState<'day' | 'week'>('day');
  const [activeTab,     setActiveTab]     = useState<'official' | 'custom'>('official');
  const [isLoading,     setIsLoading]     = useState(true);
  const [showMigration, setShowMigration] = useState(false);
  const [timetable,     setTimetable]     = useState<Record<string, any[]>>({});
  const [customClasses, setCustomClasses] = useState<CustomClass[]>([]);
  const [showCustom,    setShowCustom]    = useState(true);
  const [showChangeReq, setShowChangeReq] = useState(false);
  const [changeMsg,     setChangeMsg]     = useState('');
  const [changeEmail,   setChangeEmail]   = useState('');
  const [submitting,    setSubmitting]    = useState(false);
  const [showAddClass,  setShowAddClass]  = useState(false);
  const [editingClass,  setEditingClass]  = useState<CustomClass | null>(null);
  const [classForm,     setClassForm]     = useState({ day: '', time: '', course: '', room: '', instructor: '', type: 'makeup' as 'makeup' | 'custom' });

  const days = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
  const unreadCount = dashboard ? dashboard.generateNotifications().filter((n: any) => !dashboard.readNotifications.has(n.id)).length : 0;

  // Load custom classes
  useEffect(() => {
    try { const s = localStorage.getItem('customClasses'); if (s) setCustomClasses(JSON.parse(s)); } catch {}
  }, []);

  const saveCustomClasses = (cls: CustomClass[]) => {
    setCustomClasses(cls); localStorage.setItem('customClasses', JSON.stringify(cls));
  };

  // DataProcessor helpers
  const dp = () => DataProcessor.getInstance();
  const getAllFromSchedule = (extractor: (cls: any) => string | undefined) => {
    const set = new Set<string>();
    dp().getAllPrograms().forEach(p => Object.values(p.schedule).forEach((day: any) => {
      if (Array.isArray(day)) day.forEach((c: any) => { const v = extractor(c); if (v && v !== 'TBA' && v !== 'Dummy Teacher' && v !== 'Online Class') set.add(v); });
    }));
    return Array.from(set).sort();
  };
  const allInstructors = () => getAllFromSchedule(c => c.instructor || c.teacher);
  const allRooms       = () => getAllFromSchedule(c => c.location || c.room);
  const allCourses     = () => getAllFromSchedule(c => c.course_name || c.course);
  const allTimeslots   = () => {
    const set = new Set<string>();
    dp().getAllPrograms().forEach(p => Object.values(p.schedule).forEach((day: any) => {
      if (Array.isArray(day)) day.forEach((c: any) => { if (c.time) set.add(c.time); });
    }));
    const parseMin = (t: string) => { const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i); if (!m) return 0; let h = +m[1]; if (m[3].toUpperCase() === 'PM' && h !== 12) h += 12; if (m[3].toUpperCase() === 'AM' && h === 12) h = 0; return h * 60 + +m[2]; };
    return Array.from(set).sort((a, b) => parseMin(a) - parseMin(b));
  };

  // Load timetable
  useEffect(() => {
    const load = async () => {
      await initializeData();
      if (userPreferences) {
        const userProgram = findUserProgram(dp().getAllPrograms(), userPreferences);
        if (userProgram) {
          const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
          const { schedule } = applyTimetableProfile(userProgram.schedule, { institute: 'Institute of Management Sciences', department: userProgram.department, program: userProgram.program, referenceDate: now });
          const allDays = getOrderedDays('Monday', true);
          const tt: Record<string, any[]> = {};
          allDays.forEach(day => {
            tt[day] = (schedule[day] || []).map((c: any) => ({ time: c.time, course: c.course_name || c.course, room: c.location, instructor: c.instructor || c.teacher, isCustom: false }));
          });
          setTimetable(tt); setShowMigration(false);
        } else { setShowMigration(true); }
      }
      setIsLoading(false);
    };
    load();
  }, [userPreferences, initializeData]);

  const getMerged = () => {
    const m = { ...timetable };
    if (showCustom) customClasses.forEach(c => {
      if (!m[c.day]) m[c.day] = [];
      m[c.day] = [...m[c.day], { ...c, isCustom: true }];
    });
    return m;
  };

  const handleChangeRequest = async () => {
    if (!changeMsg.trim()) { toast({ title: 'Message required', description: 'Please describe the issue', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('_subject', '[UniSchedule] Schedule Change Request');
      fd.append('_template', 'box'); fd.append('_captcha', 'false');
      fd.append('Student Name', userPreferences?.name || 'Anonymous');
      fd.append('Email', changeEmail || userPreferences?.email || 'Not provided');
      fd.append('Program', userPreferences?.program || 'N/A');
      fd.append('Semester', String(userPreferences?.semester || 'N/A'));
      fd.append('Group', userPreferences?.group || 'N/A');
      fd.append('Issue Description', changeMsg);
      const res = await fetch('https://formsubmit.co/ajax/info@unischedule.me', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      // FormSubmit returns HTTP 200 even on failure — the body is the source of truth.
      const result = await res.json().catch(() => ({ success: 'false' }));
      if (result.success !== 'true') throw new Error(result.message || 'Failed to submit');
      toast({ title: 'Request submitted!' });
      setShowChangeReq(false); setChangeMsg(''); setChangeEmail('');
    } catch { toast({ title: 'Failed to submit', variant: 'destructive' }); }
    finally { setSubmitting(false); }
  };

  const handleSaveCustomClass = () => {
    if (!classForm.day || !classForm.time || !classForm.course || classForm.time === 'custom' || classForm.course === 'custom') {
      toast({ title: 'Missing information', description: 'Day, time, and course are required', variant: 'destructive' }); return;
    }
    const data = { day: classForm.day, time: classForm.time, course: classForm.course, room: classForm.room === 'custom' ? '' : classForm.room, instructor: classForm.instructor === 'custom' ? '' : classForm.instructor, type: classForm.type };
    if (editingClass) {
      saveCustomClasses(customClasses.map(c => c.id === editingClass.id ? { ...data, id: c.id, createdAt: c.createdAt } : c));
      toast({ title: 'Class updated' });
    } else {
      saveCustomClasses([...customClasses, { ...data, id: Date.now().toString(), createdAt: Date.now() }]);
      toast({ title: 'Class added' });
    }
    setShowAddClass(false); setEditingClass(null);
    setClassForm({ day: '', time: '', course: '', room: '', instructor: '', type: 'makeup' });
  };

  const openEdit = (c: CustomClass) => {
    setEditingClass(c);
    setClassForm({ day: c.day, time: c.time, course: c.course, room: c.room, instructor: c.instructor, type: c.type });
    setShowAddClass(true);
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="lg:hidden" style={{ background: '#0A0A0A', minHeight: '100vh', padding: '20px 16px' }}>
        {[48, 44, 180, 120].map((h, i) => (
          <div key={i} style={{ background: 'rgba(255,255,255,.04)', borderRadius: 12, height: h, marginBottom: 12, animation: 'ttmpulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.1}s` }} />
        ))}
        <style>{`@keyframes ttmpulse{0%,100%{opacity:.3}50%{opacity:.7}}`}</style>
      </div>
    );
  }

  // See the note in PersonalTimetableDesktop: graduating also trips showMigration.
  if (showMigration && !graduated) return <SemesterMigrationNotice userName={userPreferences?.name} />;

  const merged   = getMerged();
  const dayCls   = merged[selectedDay] || [];
  const official = dayCls.filter(c => !c.isCustom);
  const custom   = dayCls.filter(c => c.isCustom);

  return (
    <div className="lg:hidden" style={{
      background: '#0A0A0A', fontFamily: "'Outfit',sans-serif", color: '#fff',
      minHeight: '100vh', paddingBottom: 'calc(72px + env(safe-area-inset-bottom))',
    }}>

      {/* ── Top bar ─────────────────────────────────────── */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'calc(env(safe-area-inset-top) + 10px) 16px 10px',
        borderBottom: '1px solid rgba(255,255,255,.06)',
        background: '#0A0A0A', position: 'sticky', top: 0, zIndex: 40,
      }}>
        <Logo size="sm" clickable navigateTo="/dashboard/home" />
        <div style={{ display: 'flex', gap: 7 }}>
          {!graduated && (
            <>
              {/* Report issue */}
              <TopBtn onClick={() => setShowChangeReq(true)}>
                <AlertCircle size={14} color="#F59E0B" aria-hidden />
              </TopBtn>
              {/* Add custom class */}
              <TopBtn onClick={() => { setEditingClass(null); setClassForm({ day: selectedDay, time: '', course: '', room: '', instructor: '', type: 'makeup' }); setShowAddClass(true); }}>
                <Plus size={14} color="#22C55E" aria-hidden />
              </TopBtn>
            </>
          )}
          <TopBtn onClick={() => dashboard?.setShowSearch(true)}>
            <Search size={14} aria-hidden />
          </TopBtn>
          <TopBtn badge={unreadCount} onClick={() => { if (dashboard) { dashboard.setShowNotifications(!dashboard.showNotifications); if (!dashboard.showNotifications) setTimeout(() => dashboard.markNotificationsAsRead(), 100); } }}>
            <Bell size={14} aria-hidden />
          </TopBtn>
          <button onClick={() => dashboard?.setShowUserMenu(!dashboard?.showUserMenu)}
            style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(20,184,166,.15)', border: '1px solid rgba(20,184,166,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#14B8A6', fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 500, cursor: 'pointer' }}>
            {userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}
          </button>
        </div>
      </header>

      {/* ── Scrollable content ───────────────────────────── */}
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Page title */}
        <div>
          <h1 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 22, fontWeight: 400, letterSpacing: '-.02em', lineHeight: 1, marginBottom: 4 }}>
            My Schedule
          </h1>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.1em', color: 'rgba(255,255,255,.3)' }}>
            {[userPreferences?.program, userPreferences?.semester ? `SEM ${numberToRoman(userPreferences.semester)}` : null, userPreferences?.group ? `GRP ${userPreferences.group}` : null].filter(Boolean).join(' · ')}
          </div>
          <LostAndFoundShortcut compact />
        </div>

        {graduated && (
          <GraduatedTimetableState userName={userPreferences?.name} semesterLabel={gradSemesterLabel} />
        )}

        {!graduated && (
        <>

        {/* View toggle */}
        <div style={{ display: 'flex', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 9, overflow: 'hidden' }}>
          {(['day','week'] as const).map((m, i) => (
            <button key={m} onClick={() => setViewMode(m)} style={{
              flex: 1, padding: '8px', border: 'none', cursor: 'pointer',
              fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              background: viewMode === m ? 'rgba(20,184,166,.15)' : 'transparent',
              color: viewMode === m ? '#14B8A6' : 'rgba(255,255,255,.35)',
              borderRight: i === 0 ? '1px solid rgba(255,255,255,.07)' : 'none',
              transition: '.2s',
            }}>{m}</button>
          ))}
        </div>

        {/* ── Day view ── */}
        {viewMode === 'day' && (
          <>
            {/* Day pills */}
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
              {days.map(day => (
                <button key={day} onClick={() => setSelectedDay(day)} style={{
                  padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.08em',
                  whiteSpace: 'nowrap', transition: '.2s',
                  background: selectedDay === day ? 'rgba(20,184,166,.15)' : 'rgba(255,255,255,.04)',
                  color: selectedDay === day ? '#14B8A6' : 'rgba(255,255,255,.4)',
                }}>{day.substring(0,3).toUpperCase()}</button>
              ))}
            </div>

            {/* Official / Custom tab pills */}
            <div style={{ display: 'flex', gap: 6 }}>
              {(['official','custom'] as const).map(t => (
                <button key={t} onClick={() => setActiveTab(t)} style={{
                  flex: 1, padding: '8px', borderRadius: 9, cursor: 'pointer',
                  fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
                  background: activeTab === t ? 'rgba(20,184,166,.12)' : 'rgba(255,255,255,.04)',
                  color: activeTab === t ? '#14B8A6' : 'rgba(255,255,255,.3)',
                  border: `1px solid ${activeTab === t ? 'rgba(20,184,166,.2)' : 'rgba(255,255,255,.07)'}`,
                  transition: '.2s',
                }}>
                  {t === 'official' ? `Official (${official.length})` : `Custom (${custom.length})`}
                </button>
              ))}
            </div>

            {/* Official tab */}
            {activeTab === 'official' && (
              <div>
                <SectionHeader
                  title="Official Classes"
                  meta={`${selectedDay.substring(0,3).toUpperCase()} · ${official.length}`}
                />
                {official.length === 0
                  ? <EmptyState text={`No official classes on ${selectedDay}`} />
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {official.map((cls, i) => <ClassCard key={i} cls={cls} />)}
                    </div>
                }
              </div>
            )}

            {/* Custom tab */}
            {activeTab === 'custom' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontFamily: "'Instrument Serif',serif", fontSize: 17, fontWeight: 400, letterSpacing: '-.01em' }}>Custom & Makeup</span>
                  <button onClick={() => { setEditingClass(null); setClassForm({ day: selectedDay, time: '', course: '', room: '', instructor: '', type: 'makeup' }); setShowAddClass(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 7, border: '1px solid rgba(20,184,166,.2)', background: 'rgba(20,184,166,.08)', color: '#14B8A6', fontSize: 11, fontFamily: "'Outfit',sans-serif", cursor: 'pointer' }}>
                    <Plus size={11} aria-hidden /> Add
                  </button>
                </div>
                {custom.length === 0
                  ? <EmptyState text="No custom classes for this day"
                      onAdd={() => { setEditingClass(null); setClassForm({ day: selectedDay, time: '', course: '', room: '', instructor: '', type: 'makeup' }); setShowAddClass(true); }} />
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {custom.map(cls => (
                        <ClassCard key={cls.id} cls={cls} isCustom
                          onEdit={() => openEdit(cls)}
                          onDelete={() => { saveCustomClasses(customClasses.filter(c => c.id !== cls.id)); toast({ title: 'Class removed' }); }} />
                      ))}
                    </div>
                }
              </div>
            )}
          </>
        )}

        {/* ── Week view ── */}
        {viewMode === 'week' && (
          <div>
            <SectionHeader title="Full Week" meta="SCHEDULE" />
            <div style={{ overflowX: 'auto', marginRight: -16, paddingRight: 16 }}>
              <div style={{ minWidth: 560 }}>
                {/* Day headers */}
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 6, marginBottom: 8 }}>
                  {days.map(day => (
                    <div key={day} style={{
                      padding: '6px 6px', textAlign: 'center',
                      fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.1em',
                      background: 'rgba(255,255,255,.04)', borderRadius: 7,
                      color: day === new Date().toLocaleDateString('en-US', { weekday: 'long' }) ? '#14B8A6' : 'rgba(255,255,255,.4)',
                    }}>{day.substring(0,3).toUpperCase()}</div>
                  ))}
                </div>
                {/* Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 6 }}>
                  {days.map(day => (
                    <div key={day} style={{ display: 'flex', flexDirection: 'column', gap: 5, minHeight: 120 }}>
                      {(timetable[day] || []).map((cls, i) => <WeekCell key={i} cls={cls} />)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        </>
        )}

      </div>

      {/* ── Change Request Dialog ── */}
      <Dialog open={showChangeReq} onOpenChange={setShowChangeReq}>
        <DialogContent style={{ background: '#111', border: '1px solid rgba(255,255,255,.1)', borderRadius: 14, color: '#fff', margin: '0 16px', width: 'calc(100% - 32px)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif',serif", fontWeight: 400, fontSize: 20 }}>Report Issue</DialogTitle>
            <DialogDescription style={{ color: 'rgba(255,255,255,.4)', fontSize: 12 }}>Found an error in your schedule? Let us know.</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '6px 0' }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 6 }}>EMAIL (OPTIONAL)</div>
              <input type="email" value={changeEmail} onChange={e => setChangeEmail(e.target.value)} placeholder="your@email.com"
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 9, padding: '10px 12px', fontSize: 12, color: '#fff', fontFamily: "'Outfit',sans-serif", outline: 'none' }} />
            </div>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 6 }}>DESCRIBE THE ISSUE *</div>
              <textarea value={changeMsg} onChange={e => setChangeMsg(e.target.value)} rows={4} placeholder="e.g. Monday 10:00 AM class should be in Room LR-105…"
                style={{ width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 9, padding: '10px 12px', fontSize: 12, color: '#fff', fontFamily: "'Outfit',sans-serif", outline: 'none', resize: 'none' }} />
            </div>
          </div>
          <DialogFooter style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowChangeReq(false)} style={{ flex: 1, padding: '10px', borderRadius: 9, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', color: 'rgba(255,255,255,.5)', fontSize: 12, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Cancel</button>
            <button onClick={handleChangeRequest} disabled={submitting} style={{ flex: 1, padding: '10px', borderRadius: 9, background: '#14B8A6', border: 'none', color: '#0A0A0A', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'Outfit',sans-serif", opacity: submitting ? .6 : 1 }}>
              {submitting ? 'Sending…' : 'Submit'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add/Edit Class Dialog ── */}
      <Dialog open={showAddClass} onOpenChange={v => { setShowAddClass(v); if (!v) setEditingClass(null); }}>
        <DialogContent style={{ background: '#111', border: '1px solid rgba(255,255,255,.1)', borderRadius: 14, color: '#fff', margin: '0 16px', width: 'calc(100% - 32px)', maxHeight: '88vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Instrument Serif',serif", fontWeight: 400, fontSize: 20 }}>{editingClass ? 'Edit' : 'Add'} Class</DialogTitle>
            <DialogDescription style={{ color: 'rgba(255,255,255,.4)', fontSize: 12 }}>Makeup or custom class only visible to you</DialogDescription>
          </DialogHeader>
          <div style={{ padding: '6px 0' }}>
            {/* Type */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 8 }}>CLASS TYPE</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['makeup','custom'] as const).map(t => (
                  <button key={t} onClick={() => setClassForm(f => ({ ...f, type: t }))} style={{
                    flex: 1, padding: '9px 8px', borderRadius: 9, cursor: 'pointer',
                    border: `1px solid ${classForm.type === t ? (t === 'makeup' ? 'rgba(245,158,11,.4)' : 'rgba(34,197,94,.4)') : 'rgba(255,255,255,.08)'}`,
                    background: classForm.type === t ? (t === 'makeup' ? 'rgba(245,158,11,.1)' : 'rgba(34,197,94,.1)') : 'rgba(255,255,255,.03)',
                    color: classForm.type === t ? (t === 'makeup' ? '#F59E0B' : '#22C55E') : 'rgba(255,255,255,.4)',
                    fontSize: 12, fontFamily: "'Outfit',sans-serif", transition: '.2s',
                  }}>{t === 'makeup' ? 'Makeup' : 'Custom'}</button>
                ))}
              </div>
            </div>

            {/* Day pills */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.12em', color: 'rgba(255,255,255,.35)', marginBottom: 8 }}>DAY <span style={{ color: 'rgba(239,68,68,.6)' }}>*</span></div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => (
                  <button key={d} onClick={() => setClassForm(f => ({ ...f, day: d }))} style={{
                    padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
                    fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.06em',
                    border: `1px solid ${classForm.day === d ? 'rgba(20,184,166,.35)' : 'rgba(255,255,255,.08)'}`,
                    background: classForm.day === d ? 'rgba(20,184,166,.12)' : 'rgba(255,255,255,.03)',
                    color: classForm.day === d ? '#14B8A6' : 'rgba(255,255,255,.35)',
                    transition: '.15s',
                  }}>{d.substring(0,3)}</button>
                ))}
              </div>
            </div>

            <ComboField label="Time" value={classForm.time} options={allTimeslots()} onChange={v => setClassForm(f => ({ ...f, time: v }))} required />
            <ComboField label="Course" value={classForm.course} options={allCourses()} onChange={v => setClassForm(f => ({ ...f, course: v }))} required />
            <ComboField label="Room" value={classForm.room} options={allRooms()} onChange={v => setClassForm(f => ({ ...f, room: v }))} />
            <ComboField label="Instructor" value={classForm.instructor} options={allInstructors()} onChange={v => setClassForm(f => ({ ...f, instructor: v }))} />
          </div>
          <DialogFooter style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setShowAddClass(false); setEditingClass(null); }} style={{ flex: 1, padding: '10px', borderRadius: 9, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', color: 'rgba(255,255,255,.5)', fontSize: 12, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Cancel</button>
            <button onClick={handleSaveCustomClass} style={{ flex: 1, padding: '10px', borderRadius: 9, background: '#14B8A6', border: 'none', color: '#0A0A0A', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>{editingClass ? 'Update' : 'Add'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`@keyframes ttmpulse{0%,100%{opacity:.3}50%{opacity:.7}}`}</style>
    </div>
  );
}
